import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import Table from 'react-bootstrap/Table';
import ContractListPlayerRow from './components/ContractListPlayerRow';
import ContractSummary from './components/ContractSummary';
import LeagueNavbar from '../shared/LeagueNavbar';
import PlayerListFilters, {
  ALL,
  playerMatchesPosition,
} from '../shared/PlayerListFilters';
import './ContractList.css';

const marketValue = (player) => {
  if (player.auction_value == null) return null;
  const value = Number(player.auction_value);
  return Number.isFinite(value) ? value : null;
};

const surplusValue = (player) => {
  const market = marketValue(player);
  if (market == null || player.salary == null) return null;
  const salary = Number(player.salary);
  return Number.isFinite(salary) ? market - salary : null;
};

const ContractList = () => {
  const { league_key } = useParams();
  const [data, setData] = useState(null);
  const [positionFilter, setPositionFilter] = useState(ALL);
  const [teamFilter, setTeamFilter] = useState(ALL);
  const [sort, setSort] = useState({ key: 'salary', direction: 'desc' });

  useEffect(() => {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/api/league/${league_key}/contract_list`;
    fetch(apiUrl)
      .then(response => response.json())
      .then(data => {
        setData(data);
        console.log(data);
      });
  }, [league_key]);

  const teamNameByKey = useMemo(() => {
    const map = {};
    (data?.teams || []).forEach((team) => {
      map[team.team_key] = team.team_name;
    });
    return map;
  }, [data?.teams]);

  const filteredPlayers = useMemo(() => {
    return (data?.contract_list || []).filter((player) => {
      const positionMatches = playerMatchesPosition(player, positionFilter);
      const teamMatches = teamFilter === ALL || player.team_key === teamFilter;
      return positionMatches && teamMatches;
    });
  }, [data?.contract_list, positionFilter, teamFilter]);

  const filteredTeams = useMemo(() => {
    if (teamFilter === ALL) return data?.teams || [];
    return (data?.teams || []).filter((team) => team.team_key === teamFilter);
  }, [data?.teams, teamFilter]);

  const displayedPlayers = useMemo(() => {
    const players = [...filteredPlayers];

    const direction = sort.direction === 'asc' ? 1 : -1;
    players.sort((a, b) => {
      if (sort.key === 'contract_length') {
        return (Number(a.contract_length) - Number(b.contract_length)) * direction;
      }
      if (sort.key === 'salary') {
        return (Number(a.salary) - Number(b.salary)) * direction;
      }
      if (sort.key === 'market_value') {
        const marketA = marketValue(a);
        const marketB = marketValue(b);
        if (marketA == null) return marketB == null ? 0 : 1;
        if (marketB == null) return -1;
        return (Math.round(marketA) - Math.round(marketB)) * direction;
      }
      if (sort.key === 'surplus') {
        const surplusA = surplusValue(a);
        const surplusB = surplusValue(b);
        if (surplusA == null) return surplusB == null ? 0 : 1;
        if (surplusB == null) return -1;
        return (Math.round(surplusA) - Math.round(surplusB)) * direction;
      }
      if (sort.key === 'team') {
        const nameA = teamNameByKey[a.team_key] || '';
        const nameB = teamNameByKey[b.team_key] || '';
        return nameA.localeCompare(nameB) * direction;
      }
      return 0;
    });

    return players;
  }, [filteredPlayers, sort, teamNameByKey]);

  const toggleSort = (key) => {
    setSort((current) => ({
      key,
      direction: current.key === key
        ? (current.direction === 'asc' ? 'desc' : 'asc')
        : (key === 'team' ? 'asc' : 'desc'),
    }));
  };

  const sortIndicator = (key) => {
    if (sort.key !== key) return '';
    return sort.direction === 'asc' ? ' ▲' : ' ▼';
  };

  return (
    <div>
      {data && <LeagueNavbar teams={data.teams} league_key={league_key} />}

      <div className='page-content'>
        <PlayerListFilters
          teams={data?.teams || []}
          positionFilter={positionFilter}
          onPositionChange={setPositionFilter}
          teamFilter={teamFilter}
          onTeamChange={setTeamFilter}
        />

        <ContractSummary
          teams={filteredTeams}
          players={filteredPlayers}
          selectedTeam={teamFilter}
          onTeamSelect={setTeamFilter}
        />

        <div>
          <Table responsive bordered hover className='table'>
            <thead>
              <tr>
                <th className="name-th">
                  Player
                </th>
                <th>
                  <button
                    type="button"
                    className="sortable-th"
                    onClick={() => toggleSort('contract_length')}
                  >
                    Years{sortIndicator('contract_length')}
                  </button>
                </th>
                <th>
                  <button
                    type="button"
                    className="sortable-th"
                    onClick={() => toggleSort('salary')}
                  >
                    Salary{sortIndicator('salary')}
                  </button>
                </th>
                <th>
                  <button
                    type="button"
                    className="sortable-th"
                    onClick={() => toggleSort('market_value')}
                  >
                    Market Value{sortIndicator('market_value')}
                  </button>
                </th>
                <th>
                  <button
                    type="button"
                    className="sortable-th"
                    onClick={() => toggleSort('surplus')}
                  >
                    Surplus{sortIndicator('surplus')}
                  </button>
                </th>
                <th>
                  <button
                    type="button"
                    className="sortable-th"
                    onClick={() => toggleSort('team')}
                  >
                    Team{sortIndicator('team')}
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {displayedPlayers.map((player_contract) => (
                <ContractListPlayerRow
                  key={`${player_contract.team_key}-${player_contract.player_key}`}
                  player={player_contract}
                />
              ))}
            </tbody>
          </Table>
        </div>

      </div>
    </div>
  );
};

export default ContractList;
